import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Event as EventIcon,
  ConfirmationNumber as TicketIcon,
  Store as VendorIcon,
  AccountBalanceWallet as WalletIcon,
  Analytics as AnalyticsIcon,
  People as PeopleIcon,
  Nfc as NfcIcon,
  EmojiEvents as GamificationIcon,
  Loyalty as LoyaltyIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ModuleCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ title, description, icon, path, color }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <CardActionArea
        onClick={() => navigate(path)}
        sx={{ height: '100%', p: 2 }}
      >
        <CardContent>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.1),
              color: color,
              mb: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const EventsCVDashboard: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const modules: ModuleCardProps[] = [
    {
      title: t('events.eventsModule'),
      description: t('events.eventsDescription'),
      icon: <EventIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/events',
      color: theme.palette.primary.main,
    },
    {
      title: t('events.tickets'),
      description: t('events.ticketsDescription'),
      icon: <TicketIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/tickets',
      color: theme.palette.secondary.main,
    },
    {
      title: t('events.vendors'),
      description: t('events.vendorsDescription'),
      icon: <VendorIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/vendors',
      color: '#FF9800',
    },
    {
      title: t('events.wallet'),
      description: t('events.walletDescription'),
      icon: <WalletIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/wallet',
      color: '#4CAF50',
    },
    {
      title: t('events.nfc'),
      description: t('events.nfcDescription'),
      icon: <NfcIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/nfc',
      color: '#2196F3',
    },
    {
      title: t('events.gamification'),
      description: t('events.gamificationDescription'),
      icon: <GamificationIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/gamification',
      color: '#9C27B0',
    },
    {
      title: t('events.loyalty'),
      description: t('events.loyaltyDescription'),
      icon: <LoyaltyIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/loyalty',
      color: '#E91E63',
    },
    {
      title: t('events.chat'),
      description: t('events.chatDescription'),
      icon: <ChatIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/chat',
      color: '#00BCD4',
    },
    {
      title: t('events.attendeesModule'),
      description: t('events.attendeesDescription'),
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/attendees',
      color: '#607D8B',
    },
    {
      title: t('events.analytics'),
      description: t('events.analyticsDescription'),
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      path: '/eventscv/analytics',
      color: '#795548',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {t('events.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('events.subtitle')}
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center', flex: '1 1 200px', minWidth: 150 }}>
          <Typography variant="h3" color="primary" fontWeight="bold">
            0
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('events.activeEvents')}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, textAlign: 'center', flex: '1 1 200px', minWidth: 150 }}>
          <Typography variant="h3" color="secondary" fontWeight="bold">
            0
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('events.ticketsSold')}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, textAlign: 'center', flex: '1 1 200px', minWidth: 150 }}>
          <Typography variant="h3" sx={{ color: '#4CAF50' }} fontWeight="bold">
            0
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('events.revenue')}
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, textAlign: 'center', flex: '1 1 200px', minWidth: 150 }}>
          <Typography variant="h3" sx={{ color: '#FF9800' }} fontWeight="bold">
            0
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('events.attendees')}
          </Typography>
        </Paper>
      </Box>

      {/* Module Cards */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {t('events.modules')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {modules.map((module) => (
          <Box key={module.title} sx={{ flex: '1 1 250px', maxWidth: 300 }}>
            <ModuleCard {...module} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EventsCVDashboard;
